/**
 * PiecesPage - Gestion des pièces détachées
 * Affiche le cache pièces avec possibilité de synchroniser depuis AS400
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshIcon,
  CloseIcon,
  BoxIcon,
  SearchIcon,
  ShieldIcon
} from '../icons';
import { useViewport } from '../hooks/useViewport';
import ResponsiveCardList from '../components/responsive/ResponsiveCardList';
import { useHasPermission } from '../SupabaseWrapper';

// Types
interface Piece {
  id: string;
  code_fournisseur: string;
  libelle_fournisseur: string | null;
  code_article: string;
  designation: string;
  code_ean: string;
  famille_code: string;
  famille_libelle: string;
  rayon_code: string;
  rayon_libelle: string;
  hash: string;
  sync_status: 'synced' | 'pending' | 'error';
  sync_error?: string;
  last_synced_at?: string;
  mobile_visible: boolean;
}

interface CacheStats {
  total: number;
  byStatus: {
    synced: number;
    pending: number;
    error: number;
  };
  byFamille: Record<string, number>;
  lastSyncAt: string | null;
  globalHash: string | null;
}

interface PiecesData {
  success: boolean;
  dossier: string;
  stats: CacheStats;
  pieces: Piece[];
}

// Icone mobile (telephone)
const MobileIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

// Icone mobile barre (masque)
const MobileOffIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
    <line x1="3" y1="3" x2="21" y2="21" />
  </svg>
);

// Icone oeil (visible)
const EyeIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Icone oeil barre (masque)
const EyeOffIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// Configuration API
const API_BASE = window.location.origin;

// Props du composant
interface PiecesPageProps {
  refreshTrigger?: number;
}

// Carte mobile pour une pièce (rendue sur < 768px via ResponsiveCardList)
function PieceMobileCard({
  piece,
  isSelected,
  onToggleSelect,
}: {
  piece: Piece;
  isSelected: boolean;
  onToggleSelect: () => void;
}) {
  const supplier = piece.libelle_fournisseur || piece.code_fournisseur;
  return (
    <div
      style={{
        backgroundColor: isSelected ? '#eff6ff' : (!piece.mobile_visible ? '#fefce8' : 'white'),
        borderRadius: 12,
        padding: 14,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: isSelected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        opacity: piece.mobile_visible ? 1 : 0.85,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
            style={{ accentColor: '#3b82f6', cursor: 'pointer', width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
            aria-label="Sélectionner cet article"
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 13,
              fontFamily: 'monospace',
              fontWeight: 600,
              color: '#1e293b',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {piece.code_article}
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
              {piece.designation || '—'}
            </div>
          </div>
        </div>
        <div
          title={piece.mobile_visible ? 'Visible sur mobile' : 'Masqué sur mobile'}
          style={{
            padding: 8,
            borderRadius: 6,
            border: '1px solid ' + (piece.mobile_visible ? '#86efac' : '#e2e8f0'),
            backgroundColor: piece.mobile_visible ? '#ecfdf5' : 'white',
            color: piece.mobile_visible ? '#15803d' : '#94a3b8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {piece.mobile_visible ? <MobileIcon size={16} /> : <MobileOffIcon size={16} />}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: '#64748b' }}>
        {piece.famille_libelle && (
          <span>Famille : <strong style={{ color: '#475569' }}>{piece.famille_libelle}</strong></span>
        )}
        {piece.rayon_libelle && (
          <span>Rayon : <strong style={{ color: '#475569' }}>{piece.rayon_libelle}</strong></span>
        )}
        {supplier && (
          <span>Fournisseur : <strong style={{ color: '#475569' }}>{supplier}</strong></span>
        )}
        {piece.code_ean && (
          <span style={{ fontFamily: 'monospace', color: '#059669' }}>EAN : {piece.code_ean}</span>
        )}
      </div>
      {piece.sync_status === 'error' && piece.sync_error && (
        <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
          ⚠ {piece.sync_error}
        </div>
      )}
    </div>
  );
}

function PiecesPage({ refreshTrigger = 0 }: PiecesPageProps) {
  const { isPhone } = useViewport();
  // RBAC : permissions
  const canView = useHasPermission('pieces.view');
  const canManageMobileVis = useHasPermission('pieces.manage_mobile_visibility');

  const [data, setData] = useState<PiecesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [stale, setStale] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFournisseur, setFilterFournisseur] = useState<string[]>([]);
  const [filterFamilleLibelle, setFilterFamilleLibelle] = useState<string[]>([]);
  const [filterRayonLibelle, setFilterRayonLibelle] = useState<string[]>([]);
  const [filterMobileVisible, setFilterMobileVisible] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedIndexRef = React.useRef<number | null>(null);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = localStorage.getItem('servup_pieces_pageSize');
    return saved ? parseInt(saved) : 20;
  });
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({ show: false, type: 'success', message: '' });

  // États pour les dropdowns ouverts
  const [fournisseurDropdownOpen, setFournisseurDropdownOpen] = useState(false);
  const [familleDropdownOpen, setFamilleDropdownOpen] = useState(false);
  const [rayonDropdownOpen, setRayonDropdownOpen] = useState(false);
  const [fournisseurSearch, setFournisseurSearch] = useState('');
  const [familleSearch, setFamilleSearch] = useState('');
  const [rayonSearch, setRayonSearch] = useState('');

  // Afficher un toast temporaire
  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: 'success', message: '' }), 4000);
  };

  // Recuperer le token SSO
  const getToken = () => localStorage.getItem('sso_token');

  // Charger les donnees
  const fetchPieces = useCallback(async (notify: boolean = false) => {
    const token = getToken();
    if (!token) {
      setError('Session expirée');
      setLoading(false);
      return;
    }

    try {
      console.log('[PiecesPage] Chargement des pièces...');

      const response = await fetch(`${API_BASE}/api/pieces/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const statsResult = await response.json();
      console.log('[PiecesPage] Stats reçues:', statsResult);

      // Charger aussi la liste des pièces (toutes, pagination cote client)
      const piecesResponse = await fetch(`${API_BASE}/api/pieces?limit=50000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!piecesResponse.ok) {
        throw new Error(`Erreur HTTP ${piecesResponse.status}`);
      }

      const piecesResult = await piecesResponse.json();
      console.log('[PiecesPage] Pièces reçues:', piecesResult.pieces?.length || 0);

      if (statsResult.success) {
        setData({
          success: true,
          dossier: statsResult.dossier,
          stats: statsResult.stats,
          pieces: piecesResult.pieces || []
        });
        setError(null);
        if (notify) showToast('success', 'Données actualisées');
      } else {
        throw new Error(statsResult.error?.message || 'Erreur inconnue');
      }

      setLastRefresh(new Date());
      setStale(false);
    } catch (err: any) {
      console.error('[PiecesPage] Erreur:', err);
      setError(err.message);
      if (notify) showToast('error', `Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Modifier la visibilite mobile en masse
  const updateMobileVisibility = async (visible: boolean) => {
    const token = getToken();
    if (!token || selectedIds.size === 0) return;

    setUpdatingVisibility(true);
    try {
      const response = await fetch(`${API_BASE}/api/pieces/mobile-visibility`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ articleIds: Array.from(selectedIds), visible })
      });
      const result = await response.json();
      if (result.success) {
        // Mettre a jour localement
        setData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            pieces: prev.pieces.map(p =>
              selectedIds.has(p.id) ? { ...p, mobile_visible: visible } : p
            )
          };
        });
        showToast('success', `${result.updated} article(s) ${visible ? 'rendu(s) visible(s)' : 'masqué(s)'} sur App mobile`);
        setSelectedIds(new Set());
      } else {
        showToast('error', result.error?.message || 'Erreur');
      }
    } catch (err: any) {
      showToast('error', `Erreur: ${err.message}`);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  // Charger au montage
  useEffect(() => {
    fetchPieces();
  }, [fetchPieces]);

  // Refetch quand refreshTrigger change (navigation vers cet onglet)
  // Cache : ne recharge que si les données ont plus de 5 minutes
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    // Skip si données récentes (< 5 min)
    if (data && lastRefresh && (Date.now() - lastRefresh.getTime() < 5 * 60 * 1000)) {
      console.log('[PiecesPage] refreshTrigger changed, données récentes — skip');
      return;
    }
    console.log('[PiecesPage] refreshTrigger changed, refetching...');
    fetchPieces(false);
  }, [refreshTrigger, fetchPieces]);

  // Reset page et selection quand les filtres changent
  React.useEffect(() => { setCurrentPage(1); setSelectedIds(new Set()); }, [searchTerm, filterFournisseur, filterFamilleLibelle, filterRayonLibelle, filterMobileVisible]);

  // Filtrer les pièces
  const filteredPieces = data?.pieces?.filter(piece => {
    const matchSearch = searchTerm === '' ||
      (piece.code_article || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (piece.designation || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (piece.code_fournisseur || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (piece.libelle_fournisseur && piece.libelle_fournisseur.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (piece.code_ean && piece.code_ean.includes(searchTerm));

    const matchFournisseur = filterFournisseur.length === 0 || filterFournisseur.includes(piece.libelle_fournisseur || piece.code_fournisseur);
    const matchFamilleLibelle = filterFamilleLibelle.length === 0 || filterFamilleLibelle.includes(piece.famille_libelle);
    const matchRayonLibelle = filterRayonLibelle.length === 0 || filterRayonLibelle.includes(piece.rayon_libelle);
    const matchMobile = filterMobileVisible === 'all' ||
      (filterMobileVisible === 'visible' && piece.mobile_visible) ||
      (filterMobileVisible === 'hidden' && !piece.mobile_visible);

    return matchSearch && matchFournisseur && matchFamilleLibelle && matchRayonLibelle && matchMobile;
  }) || [];

  // Pagination calculee une fois (utilisee par grille desktop ET cartes mobiles)
  const totalPages = Math.max(1, Math.ceil(filteredPieces.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPieces = filteredPieces.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Liste des fournisseurs uniques (triés, sans vides)
  const fournisseurLabels = data?.pieces
    ? [...new Set(data.pieces.map(a => a.libelle_fournisseur || a.code_fournisseur).filter(Boolean))].sort()
    : [];

  // Liste des libellés famille uniques (triés, sans vides)
  const familleLibelles = data?.pieces
    ? [...new Set(data.pieces.map(a => a.famille_libelle).filter(Boolean))].sort()
    : [];

  // Liste des libellés rayon uniques (triés, sans vides)
  const rayonLibelles = data?.pieces
    ? [...new Set(data.pieces.map(a => a.rayon_libelle).filter(Boolean))].sort()
    : [];

  // Fermer les dropdowns quand on clique ailleurs
  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.multi-select-dropdown')) {
      setFournisseurDropdownOpen(false);
      setFamilleDropdownOpen(false);
      setRayonDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!canView) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Vous n'avez pas les autorisations nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <RefreshIcon size={32} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '12px', color: '#64748b' }}>Chargement des pièces...</span>
      </div>
    );
  }

  // Compteurs visibilite mobile
  const mobileVisibleCount = data?.pieces?.filter(p => p.mobile_visible).length || 0;
  const mobileHiddenCount = data?.pieces?.filter(p => !p.mobile_visible).length || 0;

  return (
    <div style={{ padding: '0', position: 'relative' }}>
      {/* Toast notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 20px',
          backgroundColor: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#86efac' : '#fecaca'}`,
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease'
        }}>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: toast.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {toast.type === 'success' ? '✓' : '✕'}
          </span>
          <span style={{
            color: toast.type === 'success' ? '#166534' : '#dc2626',
            fontWeight: 500,
            fontSize: '14px'
          }}>
            {toast.message}
          </span>
          <button
            onClick={() => setToast({ ...toast, show: false })}
            style={{
              marginLeft: '8px',
              padding: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              fontSize: '18px',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Header avec bouton Sync */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          {/* Bouton Refresh — orange si données périmées après sync */}
          <button
            onClick={() => fetchPieces(true)}
            disabled={loading}
            title={stale ? 'Nouvelles données disponibles — cliquez pour actualiser' : 'Actualiser les données'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: stale ? '#fff7ed' : 'white',
              color: stale ? '#c2410c' : '#475569',
              border: `1px solid ${stale ? '#fb923c' : '#cbd5e1'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: stale ? 600 : 500,
              transition: 'all 0.15s ease',
              animation: stale ? 'pulse 2s ease-in-out infinite' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = stale ? '#ffedd5' : '#f8fafc';
              e.currentTarget.style.borderColor = stale ? '#f97316' : '#94a3b8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = stale ? '#fff7ed' : 'white';
              e.currentTarget.style.borderColor = stale ? '#fb923c' : '#cbd5e1';
            }}
          >
            <RefreshIcon size={18} />
            {stale ? 'Actualiser ●' : 'Actualiser'}
          </button>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CloseIcon size={18} />
          {error}
        </div>
      )}

      {/* Stats cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isPhone ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)',
        gap: isPhone ? 12 : 16,
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: '#dbeafe',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e40af' }}>
            {data?.stats?.total || 0}
          </div>
          <div style={{ fontSize: '13px', color: '#1e40af', marginTop: '4px' }}>Total pièces</div>
        </div>
        <div style={{
          backgroundColor: '#dcfce7',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#22c55e' }}>
            {mobileVisibleCount}
          </div>
          <div style={{ fontSize: '13px', color: '#166534', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
            <MobileIcon size={14} color="#166534" /> Visible sur App mobile
          </div>
        </div>
        <div style={{
          backgroundColor: '#fef3c7',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#92400e' }}>
            {mobileHiddenCount}
          </div>
          <div style={{ fontSize: '13px', color: '#92400e', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
            <MobileOffIcon size={14} color="#92400e" /> Non visible sur App mobile
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Recherche */}
        <div style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '280px' }}>
          <SearchIcon size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 12px 9px 40px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '13px',
              outline: 'none'
            }}
          />
        </div>

        {/* Filtre Fournisseur - Multi-select avec recherche */}
        <div className="multi-select-dropdown" style={{ position: 'relative' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setFournisseurDropdownOpen(!fournisseurDropdownOpen);
              setFamilleDropdownOpen(false);
              setRayonDropdownOpen(false);
            }}
            style={{
              padding: '9px 14px',
              border: `1px solid ${filterFournisseur.length > 0 ? '#6366f1' : '#e2e8f0'}`,
              borderRadius: '8px',
              fontSize: '13px',
              backgroundColor: filterFournisseur.length > 0 ? '#eef2ff' : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '160px'
            }}
          >
            <span style={{ color: filterFournisseur.length > 0 ? '#4f46e5' : '#64748b' }}>
              Fournisseur {filterFournisseur.length > 0 && `(${filterFournisseur.length})`}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
          </button>
          {fournisseurDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100,
              minWidth: '280px',
              maxHeight: '320px',
              overflow: 'hidden'
            }}>
              <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  type="text"
                  placeholder="Rechercher un fournisseur..."
                  value={fournisseurSearch}
                  onChange={(e) => setFournisseurSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>
              <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px 0' }}>
                {fournisseurLabels
                  .filter(f => f.toLowerCase().includes(fournisseurSearch.toLowerCase()))
                  .map(f => (
                    <label
                      key={f}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        backgroundColor: filterFournisseur.includes(f) ? '#eef2ff' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = filterFournisseur.includes(f) ? '#eef2ff' : 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={filterFournisseur.includes(f)}
                        onChange={() => {
                          setFilterFournisseur(prev =>
                            prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]
                          );
                        }}
                        style={{ accentColor: '#6366f1' }}
                      />
                      <span style={{ color: '#374151' }}>{f}</span>
                    </label>
                  ))}
              </div>
              {filterFournisseur.length > 0 && (
                <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    onClick={() => setFilterFournisseur([])}
                    style={{
                      width: '100%',
                      padding: '6px',
                      backgroundColor: '#f1f5f9',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#64748b',
                      cursor: 'pointer'
                    }}
                  >
                    Effacer la sélection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filtre Famille - Multi-select avec recherche */}
        <div className="multi-select-dropdown" style={{ position: 'relative' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setFamilleDropdownOpen(!familleDropdownOpen);
              setFournisseurDropdownOpen(false);
              setRayonDropdownOpen(false);
            }}
            style={{
              padding: '9px 14px',
              border: `1px solid ${filterFamilleLibelle.length > 0 ? '#8b5cf6' : '#e2e8f0'}`,
              borderRadius: '8px',
              fontSize: '13px',
              backgroundColor: filterFamilleLibelle.length > 0 ? '#f5f3ff' : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '160px'
            }}
          >
            <span style={{ color: filterFamilleLibelle.length > 0 ? '#7c3aed' : '#64748b' }}>
              Famille {filterFamilleLibelle.length > 0 && `(${filterFamilleLibelle.length})`}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
          </button>
          {familleDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100,
              minWidth: '280px',
              maxHeight: '320px',
              overflow: 'hidden'
            }}>
              <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  type="text"
                  placeholder="Rechercher une famille..."
                  value={familleSearch}
                  onChange={(e) => setFamilleSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>
              <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px 0' }}>
                {familleLibelles
                  .filter(f => f.toLowerCase().includes(familleSearch.toLowerCase()))
                  .map(f => (
                    <label
                      key={f}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        backgroundColor: filterFamilleLibelle.includes(f) ? '#f5f3ff' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = filterFamilleLibelle.includes(f) ? '#f5f3ff' : 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={filterFamilleLibelle.includes(f)}
                        onChange={() => {
                          setFilterFamilleLibelle(prev =>
                            prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]
                          );
                        }}
                        style={{ accentColor: '#8b5cf6' }}
                      />
                      <span style={{ color: '#374151' }}>{f}</span>
                    </label>
                  ))}
              </div>
              {filterFamilleLibelle.length > 0 && (
                <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    onClick={() => setFilterFamilleLibelle([])}
                    style={{
                      width: '100%',
                      padding: '6px',
                      backgroundColor: '#f1f5f9',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#64748b',
                      cursor: 'pointer'
                    }}
                  >
                    Effacer la sélection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filtre Rayon - Multi-select avec recherche */}
        <div className="multi-select-dropdown" style={{ position: 'relative' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRayonDropdownOpen(!rayonDropdownOpen);
              setFournisseurDropdownOpen(false);
              setFamilleDropdownOpen(false);
            }}
            style={{
              padding: '9px 14px',
              border: `1px solid ${filterRayonLibelle.length > 0 ? '#0ea5e9' : '#e2e8f0'}`,
              borderRadius: '8px',
              fontSize: '13px',
              backgroundColor: filterRayonLibelle.length > 0 ? '#f0f9ff' : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '160px'
            }}
          >
            <span style={{ color: filterRayonLibelle.length > 0 ? '#0284c7' : '#64748b' }}>
              Rayon {filterRayonLibelle.length > 0 && `(${filterRayonLibelle.length})`}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
          </button>
          {rayonDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100,
              minWidth: '280px',
              maxHeight: '320px',
              overflow: 'hidden'
            }}>
              <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  type="text"
                  placeholder="Rechercher un rayon..."
                  value={rayonSearch}
                  onChange={(e) => setRayonSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>
              <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px 0' }}>
                {rayonLibelles
                  .filter(r => r.toLowerCase().includes(rayonSearch.toLowerCase()))
                  .map(r => (
                    <label
                      key={r}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        backgroundColor: filterRayonLibelle.includes(r) ? '#f0f9ff' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = filterRayonLibelle.includes(r) ? '#f0f9ff' : 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={filterRayonLibelle.includes(r)}
                        onChange={() => {
                          setFilterRayonLibelle(prev =>
                            prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
                          );
                        }}
                        style={{ accentColor: '#0ea5e9' }}
                      />
                      <span style={{ color: '#374151' }}>{r}</span>
                    </label>
                  ))}
              </div>
              {filterRayonLibelle.length > 0 && (
                <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    onClick={() => setFilterRayonLibelle([])}
                    style={{
                      width: '100%',
                      padding: '6px',
                      backgroundColor: '#f1f5f9',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#64748b',
                      cursor: 'pointer'
                    }}
                  >
                    Effacer la sélection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filtre visibilite mobile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
          {[
            { value: 'all', label: 'Tous' },
            { value: 'visible', label: 'Visible', icon: <MobileIcon size={14} color="#22c55e" /> },
            { value: 'hidden', label: 'Masqué', icon: <MobileOffIcon size={14} color="#ef4444" /> }
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilterMobileVisible(opt.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: filterMobileVisible === opt.value ? '1px solid #3b82f6' : '1px solid #d1d5db',
                backgroundColor: filterMobileVisible === opt.value ? '#eff6ff' : 'white',
                color: filterMobileVisible === opt.value ? '#3b82f6' : '#64748b',
                fontWeight: filterMobileVisible === opt.value ? 600 : 400,
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                {'icon' in opt && opt.icon}
                {opt.label}
              </span>
            </button>
          ))}
        </div>

        {/* Compteur resultats */}
        <div style={{ fontSize: '13px', color: '#64748b', marginLeft: 'auto' }}>
          {filteredPieces.length} pièce{filteredPieces.length > 1 ? 's' : ''}
        </div>
      </div>

      {/* Barre d'action flottante (selection active) */}
      {selectedIds.size > 0 && (
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          marginBottom: '16px',
          padding: isPhone ? '10px 12px' : '12px 20px',
          backgroundColor: '#1e293b',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: isPhone ? 'column' : 'row',
          alignItems: isPhone ? 'stretch' : 'center',
          justifyContent: isPhone ? 'flex-start' : 'space-between',
          gap: isPhone ? '8px' : 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.2s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: isPhone ? 'space-between' : 'flex-start' }}>
            <span style={{ color: 'white', fontSize: isPhone ? '13px' : '14px', fontWeight: 600 }}>
              {selectedIds.size} article{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
            </span>
            <button
              onClick={() => setSelectedIds(new Set())}
              style={{
                padding: '4px 10px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: '#94a3b8',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Désélectionner
            </button>
          </div>
          {canManageMobileVis && (
            <div style={{ display: 'flex', gap: isPhone ? '6px' : '10px' }}>
              <button
                onClick={() => updateMobileVisibility(false)}
                disabled={updatingVisibility}
                style={{
                  flex: isPhone ? 1 : undefined,
                  padding: isPhone ? '8px 10px' : '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: updatingVisibility ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  opacity: updatingVisibility ? 0.7 : 1
                }}
              >
                <EyeOffIcon size={16} color="white" /> {isPhone ? 'Masquer' : 'Masquer sur App mobile'}
              </button>
              <button
                onClick={() => updateMobileVisibility(true)}
                disabled={updatingVisibility}
                style={{
                  flex: isPhone ? 1 : undefined,
                  padding: isPhone ? '8px 10px' : '8px 16px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: updatingVisibility ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  opacity: updatingVisibility ? 0.7 : 1
                }}
              >
                <EyeIcon size={16} color="white" /> {isPhone ? 'Rendre visible' : 'Rendre visible'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tableau des pièces */}
      <div style={isPhone ? {} : {
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <ResponsiveCardList
          items={paginatedPieces}
          renderCard={(piece) => (
            <PieceMobileCard
              piece={piece}
              isSelected={selectedIds.has(piece.id)}
              onToggleSelect={() => {
                setSelectedIds(prev => {
                  const n = new Set(prev);
                  if (n.has(piece.id)) n.delete(piece.id); else n.add(piece.id);
                  return n;
                });
              }}
            />
          )}
          emptyMessage={
            searchTerm || filterFournisseur.length > 0 || filterFamilleLibelle.length > 0 || filterRayonLibelle.length > 0
              ? 'Aucune pièce trouvée. Essayez de modifier vos filtres.'
              : 'Aucune pièce à afficher.'
          }
          fallback={
            <>
              {/* En-tete tableau */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '36px 150px 110px 1fr 110px 150px 150px 40px',
                gap: '10px',
                padding: '14px 20px',
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={filteredPieces.length > 0 && filteredPieces.every(p => selectedIds.has(p.id))}
                    ref={(el) => {
                      if (el) {
                        const allSelected = filteredPieces.length > 0 && filteredPieces.every(p => selectedIds.has(p.id));
                        const someSelected = !allSelected && filteredPieces.some(p => selectedIds.has(p.id));
                        el.indeterminate = someSelected;
                      }
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(prev => { const n = new Set(prev); filteredPieces.forEach(p => n.add(p.id)); return n; });
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                    style={{ accentColor: '#3b82f6', cursor: 'pointer', width: '16px', height: '16px' }}
                    title={`Sélectionner les ${filteredPieces.length} articles filtrés`}
                  />
                </div>
                <div>Fournisseur</div>
                <div>Code Pièce</div>
                <div>Désignation</div>
                <div>Code EAN</div>
                <div>Famille</div>
                <div>Rayon</div>
                <div style={{ display: 'flex', justifyContent: 'center' }}><MobileIcon size={16} color="#64748b" /></div>
              </div>

              {/* Corps du tableau */}
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {paginatedPieces.length > 0 ? (
            paginatedPieces.map((piece) => {
              const isSelected = selectedIds.has(piece.id);
              return (
                <div
                  key={piece.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 150px 110px 1fr 110px 150px 150px 40px',
                    gap: '10px',
                    padding: '12px 20px',
                    borderBottom: '1px solid #f1f5f9',
                    fontSize: '13px',
                    alignItems: 'center',
                    backgroundColor: isSelected ? '#eff6ff' : (!piece.mobile_visible ? '#fefce8' : 'transparent'),
                    opacity: piece.mobile_visible ? 1 : 0.7
                  }}
                >
                  {/* Checkbox */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onClick={(e) => {
                        const clickedIndex = filteredPieces.findIndex(p => p.id === piece.id);
                        if (e.shiftKey && lastClickedIndexRef.current !== null) {
                          // Shift+click : selectionner la plage
                          const start = Math.min(lastClickedIndexRef.current, clickedIndex);
                          const end = Math.max(lastClickedIndexRef.current, clickedIndex);
                          setSelectedIds(prev => {
                            const n = new Set(prev);
                            for (let i = start; i <= end; i++) {
                              n.add(filteredPieces[i].id);
                            }
                            return n;
                          });
                        } else {
                          // Click simple : toggle
                          setSelectedIds(prev => {
                            const n = new Set(prev);
                            if (n.has(piece.id)) n.delete(piece.id); else n.add(piece.id);
                            return n;
                          });
                        }
                        lastClickedIndexRef.current = clickedIndex;
                      }}
                      onChange={() => {}} // gere par onClick
                      style={{ accentColor: '#3b82f6', cursor: 'pointer', width: '15px', height: '15px' }}
                    />
                  </div>

                  {/* Fournisseur */}
                  <div style={{ overflow: 'hidden' }} title={`${piece.code_fournisseur}${piece.libelle_fournisseur ? ' — ' + piece.libelle_fournisseur : ''}`}>
                    <div style={{
                      fontSize: '12px',
                      color: '#1e293b',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {piece.libelle_fournisseur || piece.code_fournisseur}
                    </div>
                    {piece.libelle_fournisseur && (
                      <div style={{ fontSize: '10px', color: '#6366f1', fontFamily: 'monospace' }}>
                        {piece.code_fournisseur}
                      </div>
                    )}
                  </div>

                  {/* Code pièce */}
                  <div style={{ fontWeight: 500, color: '#1e293b', fontFamily: 'monospace', fontSize: '10px' }}>
                    {piece.code_article}
                  </div>

                  {/* Designation */}
                  <div style={{
                    color: '#475569',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '12px'
                  }} title={piece.designation}>
                    {piece.designation}
                  </div>

                  {/* Code EAN */}
                  <div style={{
                    fontWeight: 500,
                    color: '#059669',
                    fontFamily: 'monospace',
                    fontSize: '11px'
                  }} title={piece.code_ean || 'Aucun code EAN'}>
                    {piece.code_ean || '-'}
                  </div>

                  {/* Famille */}
                  <div style={{ overflow: 'hidden' }} title={`${piece.famille_code || ''}${piece.famille_libelle ? ' — ' + piece.famille_libelle : ''}`}>
                    <div style={{
                      fontSize: '12px',
                      color: '#1e293b',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {piece.famille_libelle || piece.famille_code || '-'}
                    </div>
                    {piece.famille_libelle && piece.famille_code && (
                      <div style={{ fontSize: '10px', color: '#8b5cf6', fontFamily: 'monospace' }}>
                        {piece.famille_code}
                      </div>
                    )}
                  </div>

                  {/* Rayon */}
                  <div style={{ overflow: 'hidden' }} title={`${piece.rayon_code || ''}${piece.rayon_libelle ? ' — ' + piece.rayon_libelle : ''}`}>
                    <div style={{
                      fontSize: '12px',
                      color: '#1e293b',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {piece.rayon_libelle || piece.rayon_code || '-'}
                    </div>
                    {piece.rayon_libelle && piece.rayon_code && (
                      <div style={{ fontSize: '10px', color: '#0ea5e9', fontFamily: 'monospace' }}>
                        {piece.rayon_code}
                      </div>
                    )}
                  </div>

                  {/* Visibilite mobile */}
                  <div style={{ display: 'flex', justifyContent: 'center' }} title={piece.mobile_visible ? 'Visible sur App mobile' : 'Masqué sur App mobile'}>
                    {piece.mobile_visible
                      ? <MobileIcon size={16} color="#22c55e" />
                      : <MobileOffIcon size={16} color="#ef4444" />
                    }
                  </div>
                </div>
              );
            })
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                    <BoxIcon size={48} color="#cbd5e1" style={{ marginBottom: '12px' }} />
                    <div>Aucune pièce trouvée</div>
                    {searchTerm || filterFournisseur.length > 0 || filterFamilleLibelle.length > 0 || filterRayonLibelle.length > 0 ? (
                      <div style={{ fontSize: '13px', marginTop: '8px' }}>
                        Essayez de modifier vos filtres
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </>
          }
        />

        {/* Pagination */}
        {filteredPieces.length > 0 && (
            <div style={{
              padding: isPhone ? '12px' : '12px 20px',
              backgroundColor: isPhone ? 'transparent' : '#f8fafc',
              borderTop: isPhone ? 'none' : '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: isPhone ? 'column' : 'row',
              justifyContent: isPhone ? 'flex-start' : 'space-between',
              alignItems: isPhone ? 'stretch' : 'center',
              fontSize: '13px',
              color: '#64748b',
              gap: isPhone ? 10 : 0
            }}>
              {/* Selecteur nombre par page */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: isPhone ? 'center' : 'flex-start' }}>
                <span>Afficher</span>
                {[10, 20, 50, 100].map(size => (
                  <button
                    key={size}
                    onClick={() => {
                      setPageSize(size);
                      setCurrentPage(1);
                      localStorage.setItem('servup_pieces_pageSize', String(size));
                    }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: pageSize === size ? '1px solid #3b82f6' : '1px solid #d1d5db',
                      backgroundColor: pageSize === size ? '#eff6ff' : 'white',
                      color: pageSize === size ? '#3b82f6' : '#374151',
                      fontWeight: pageSize === size ? 600 : 400,
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    {size}
                  </button>
                ))}
                <span>par page</span>
              </div>

              {/* Info page */}
              <span style={{ textAlign: isPhone ? 'center' : 'left' }}>
                {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filteredPieces.length)} sur {filteredPieces.length}
              </span>

              {/* Navigation pages */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: isPhone ? 'center' : 'flex-start' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={safePage <= 1}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: safePage <= 1 ? '#d1d5db' : '#374151',
                    cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: safePage <= 1 ? '#d1d5db' : '#374151',
                    cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  ‹
                </button>
                <span style={{ padding: '0 8px', fontWeight: 600, color: '#1f2937' }}>
                  {safePage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: safePage >= totalPages ? '#d1d5db' : '#374151',
                    cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={safePage >= totalPages}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: safePage >= totalPages ? '#d1d5db' : '#374151',
                    cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  »»
                </button>
              </div>
            </div>
        )}
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
@keyframes slideIn {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default PiecesPage;
